// ===========================================
// Reset Teacher Tasks Dialog
// ===========================================
// Admin action to "start fresh" at term rollover: writes off all currently
// outstanding teacher tasks (incomplete teaching notes) so the task counters
// read 0. Notes themselves are NOT deleted — this records a resolved watermark
// up to today's date. Reversible by removing the latest reset.

import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Alert,
  Typography,
  CircularProgress,
  Box,
} from '@mui/material';
import { useResetTeacherTasks, useTaskReset } from '../../hooks/useUsers';

interface ResetTeacherTasksDialogProps {
  open: boolean;
  onClose: () => void;
  /** School-wide count of currently outstanding tasks, for the preview line. */
  outstandingCount: number;
}

export default function ResetTeacherTasksDialog({
  open,
  onClose,
  outstandingCount,
}: ResetTeacherTasksDialogProps) {
  const [reason, setReason] = useState('');
  const resetMutation = useResetTeacherTasks();
  const { data: lastReset } = useTaskReset();

  const handleConfirm = () => {
    resetMutation.mutate(
      { reason: reason.trim() || undefined },
      {
        onSuccess: () => {
          setReason('');
          onClose();
        },
      }
    );
  };

  const handleClose = () => {
    if (resetMutation.isPending) return;
    setReason('');
    onClose();
  };

  const lastResetLabel = lastReset
    ? `Last reset on ${new Date(lastReset.createdAt).toLocaleDateString()}${
        lastReset.resetBy ? ` by ${lastReset.resetBy.firstName} ${lastReset.resetBy.lastName}` : ''
      }`
    : null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reset Teacher Tasks</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          This writes off all currently outstanding teacher tasks (incomplete
          class and student notes) up to today, so the task counters on the
          dashboard and teacher pages return to zero — a clean slate for the new
          term.
        </DialogContentText>

        {outstandingCount > 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>{outstandingCount}</strong> outstanding{' '}
            {outstandingCount === 1 ? 'task' : 'tasks'} will be cleared.
          </Alert>
        ) : (
          <Alert severity="success" sx={{ mb: 2 }}>
            There are no outstanding tasks right now. Resetting will set today as
            the cut-off going forward.
          </Alert>
        )}

        <Alert severity="warning" sx={{ mb: 2 }}>
          Notes are <strong>not</strong> deleted — teachers can still view and
          edit them. This only stops them being counted as outstanding.
        </Alert>

        <TextField
          label="Reason (optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          placeholder="e.g. End of Term 2 — starting fresh"
          disabled={resetMutation.isPending}
        />

        {lastResetLabel && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {lastResetLabel}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={resetMutation.isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="warning"
          disabled={resetMutation.isPending}
          startIcon={resetMutation.isPending ? <CircularProgress size={16} /> : undefined}
        >
          Reset Tasks
        </Button>
      </DialogActions>
    </Dialog>
  );
}
